import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { XStack, YStack } from '@/components/ui/layout/layout';
import {
  handleEventKitConnect,
  handleOpenEventKitSettings,
} from '@/integrations/integration-handlers';
import { useEventKitState } from '@/integrations/useEventKitState';

import { DevicePermissionBanner } from './device-permission-banner';
import {
  useReminderListsQuery,
  type EventKitSource,
} from './eventkit-sources.query';

import type { ConfigurationContext } from './configuration.types';
import type { UseQueryResult } from '@tanstack/react-query';

function SourcesList({ query }: { query: UseQueryResult<EventKitSource[]> }) {
  if (query.isPending) {
    return (
      <XStack
        center
        className="gap-2 rounded-lg border border-border bg-surface px-4 py-4 dark:border-border-strong"
      >
        <Icons.loader className="size-3.5 animate-spin text-foreground-muted" />
        <span className="text-xs text-foreground-muted">Loading lists…</span>
      </XStack>
    );
  }

  if (query.isError && query.data === undefined) {
    return (
      <YStack className="items-center gap-3 rounded-lg border border-border bg-surface px-4 py-6 text-center dark:border-border-strong">
        <p className="text-xs text-foreground-muted">
          Couldn&apos;t load lists from this Mac.
        </p>
        <Button
          variant="secondary"
          size="sm"
          onClick={() => void query.refetch()}
        >
          Try again
        </Button>
      </YStack>
    );
  }

  const sources = query.data ?? [];

  return (
    <YStack className="gap-2">
      <XStack className="items-baseline gap-1.5">
        <span className="text-xs font-medium text-foreground-muted">Lists</span>
        {sources.length > 0 && (
          <span className="text-xs text-foreground-subtle">
            {sources.length}
          </span>
        )}
      </XStack>
      <YStack className="overflow-hidden rounded-lg border border-border bg-surface dark:border-border-strong">
        {sources.length === 0 ? (
          <p className="px-4 py-3 text-xs text-foreground-muted">
            No reminder lists found on this Mac.
          </p>
        ) : (
          <YStack className="divide-y divide-border dark:divide-border-strong">
            {sources.map((source) => (
              <XStack
                key={source.id}
                className="items-center gap-3 px-4 py-2.5"
              >
                <span
                  className="size-1.5 shrink-0 rounded-full"
                  style={{ backgroundColor: source.color }}
                />
                <span className="min-w-0 flex-1 truncate text-sm text-foreground">
                  {source.name}
                </span>
                {source.sourceName && (
                  <span className="shrink-0 truncate text-xs text-foreground-muted">
                    {source.sourceName}
                  </span>
                )}
              </XStack>
            ))}
          </YStack>
        )}
      </YStack>
    </YStack>
  );
}

export function ReminderListsConfiguration(_props: {
  context: ConfigurationContext;
}) {
  const status = useEventKitState().remindersStatus;
  const granted = status === 'authorized';
  const sourcesQuery = useReminderListsQuery(granted);

  return (
    <YStack className="gap-4 p-6">
      <DevicePermissionBanner
        status={status}
        domain="Reminders"
        domainPlural="reminders"
        onGrantAccess={() => handleEventKitConnect('applereminders')}
        onOpenSettings={() => handleOpenEventKitSettings('applereminders')}
      />
      {granted && <SourcesList query={sourcesQuery} />}
    </YStack>
  );
}
